/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useEffect, useMemo, useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import type { ApolloError } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

// eslint-disable-next-line import/no-cycle
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../Controller';

import type {
	useGroupsCountQuery as useGroupsCountQuery$data,
	useGroupsCountQueryVariables as useGroupsCountQuery$variables,
} from './__types__/useGroupsCountQuery';
import type { useQueriesShareAndRestrictDialogQuery as useQueriesShareAndRestrictDialogQuery$data } from './__types__/useQueriesShareAndRestrictDialogQuery';
import type { PublishModalRestrictionsInput } from './publishModalDataToQueriesState';

const useGroupsCountQuery = gql`
	query useGroupsCountQuery($groupIdList: [String]) {
		groupCounts(groupIds: $groupIdList) {
			groupCounts {
				key
				value
			}
		}
	}
`;

export type GroupAndTeamCountsMapType = Map<string, number>;

function toMap(
	pairs: { key: string; value: number; __typename?: string }[] = [],
): GroupAndTeamCountsMapType {
	return new Map(pairs.map(({ key, value }) => [key, value] as const));
}

export const useGroupsAndTeamCountQuery = ({
	data,
	publishModalRestrictions,
	error,
}: {
	data: useQueriesShareAndRestrictDialogQuery$data | undefined;
	publishModalRestrictions: PublishModalRestrictionsInput | undefined;
	error: ApolloError | undefined;
}): {
	isGroupsCountLoading: boolean;
	groupAndTeamCountsMap: GroupAndTeamCountsMapType;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [groupAndTeamCountsMap, setGroupAndTeamCountsMap] = useState<GroupAndTeamCountsMapType>(
		new Map(),
	);

	const modalType = publishModalRestrictions ? 'publishModal' : 'shareAndRestrictModal';

	const publishedModalGroupAndTeamId = useMemo(() => {
		if (!publishModalRestrictions) return [];

		const ids = new Set<string>();

		for (const item of publishModalRestrictions) {
			const groups = item?.restrictions?.group?.results ?? [];
			for (const group of groups) {
				if (group?.id) {
					ids.add(group.id);
				}
			}
		}

		return Array.from(ids);
	}, [publishModalRestrictions]);

	const groupsAndTeamsId = useMemo(() => {
		if (!data?.content?.nodes?.[0]?.restrictions) return [];

		const readNodes = data.content.nodes[0].restrictions.read?.restrictions?.group?.nodes ?? [];
		const updateNodes = data.content.nodes[0].restrictions.update?.restrictions?.group?.nodes ?? [];

		const allNodes = [...readNodes, ...updateNodes];
		const uniqueIds = new Set(allNodes.map((node) => node?.id).filter(Boolean));

		return Array.from(uniqueIds);
	}, [data]);

	const shouldSkipQuery = useMemo(() => {
		const idsToQuery =
			modalType === 'publishModal' ? publishedModalGroupAndTeamId : groupsAndTeamsId;
		return !!error || idsToQuery.length === 0;
	}, [error, modalType, publishedModalGroupAndTeamId, groupsAndTeamsId]);

	const {
		loading: isGroupsCountLoading,
		data: groupsCountData,
		error: groupsCountError,
	} = useQuery<useGroupsCountQuery$data, useGroupsCountQuery$variables>(useGroupsCountQuery, {
		variables: {
			groupIdList: modalType === 'publishModal' ? publishedModalGroupAndTeamId : groupsAndTeamsId,
		},
		skip: shouldSkipQuery,
	});

	const processGroupCountsData: GroupAndTeamCountsMapType = useMemo(() => {
		if (!groupsCountData?.groupCounts?.groupCounts) return new Map();

		const validCounts = groupsCountData.groupCounts.groupCounts
			.filter((item): item is NonNullable<typeof item> => item !== null)
			.filter(
				(item): item is { key: string; value: number } =>
					typeof item.key === 'string' && typeof item.value === 'number',
			);

		return toMap(validCounts);
	}, [groupsCountData]);

	useEffect(() => {
		if (!isGroupsCountLoading && groupsCountError) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					action: 'errored',
					actionSubject: 'useGroupsCountQuery',
					attributes: {
						message: groupsCountError?.message,
						name: groupsCountError?.name,
						modalType,
						groupCount:
							modalType === 'publishModal'
								? publishedModalGroupAndTeamId.length
								: groupsAndTeamsId.length,
					},
				},
			}).fire();
		}

		if (!isGroupsCountLoading && processGroupCountsData.size > 0) {
			setGroupAndTeamCountsMap(processGroupCountsData);
		}
	}, [
		isGroupsCountLoading,
		groupsCountData,
		groupsCountError,
		createAnalyticsEvent,
		processGroupCountsData,
		modalType,
		publishedModalGroupAndTeamId.length,
		groupsAndTeamsId.length,
	]);

	return { isGroupsCountLoading, groupAndTeamCountsMap };
};
